import React, { useState, useRef, useEffect, useMemo, FC } from 'react';
import { Dropdown, Button, InputAdornment, Input, Icon } from 'tea-component';
import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { useIntl, getLocale } from 'umi';
import classnames from 'classnames';
import './index.scss';

type NumberRange = {
  min: number | undefined;
  max: number | undefined;
}
interface NumberRangeProps {
  value: NumberRange;
  title: string;
  /** formatMessage 找不到 key 时的兜底文案 */
  titleDefaultMessage?: string;
  width?: number;
  clickSetDefault?: boolean;
  leftPlaceholder?: string;
  rightPlaceholder?: string;
  className?: string;
  unitText?: string;
  decimalPlaces?: number;
  onChange: (value: NumberRange) => void;
}

const NumberRange: FC<NumberRangeProps> = (props) => {
  const { value = { min: '', max: '' }, onChange, clickSetDefault = false,
    title, titleDefaultMessage, leftPlaceholder, rightPlaceholder, width, className,
    unitText, decimalPlaces } = props;

  const dropDown = useRef<HTMLDivElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const inputMinRef = useRef<HTMLInputElement>(null);
  const inputMaxRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [isMinError, setIsMinError] = useState(false);
  const [isMaxError, setIsMaxError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // BMK最大数值限制999.99B
  const BMK = 999999999999;
  const numberPattern = decimalPlaces === 2
    ? /^[0-9]*(\.[0-9]{0,2})?$/
    : /^[0-9]*[\.]?[0-9]*$/;

  const handleConfirm = (): void => {
    onChange({
      min: minValue ? Number(minValue) : undefined,
      max: maxValue ? Number(maxValue) : undefined,
    });
    setIsOpen(false);
  };

  useEffect(() => {
    setMinValue(value?.min ? `${value?.min}` : value?.min === 0 ? '0' : '');
    setMaxValue(value?.max ? `${value?.max}` : value?.max === 0 ? '0' : '');

  }, [JSON.stringify(value), isOpen]);

  // 点击目标元素外面判断是否关闭弹层
  const handleOutSideClick = (e: Event) => {
    // 判断点击元素是否在Dropdown组件内
    if (dropDown?.current?.contains(e.target as HTMLElement)) return;

    // 判断点击元素是否在弹层内
    if (popover?.current?.contains(e.target as HTMLElement)) return;

    setIsOpen(false);
    setIsMaxError(false);
    setIsMinError(false);
  };

  useEffect(() => {
    document.addEventListener('click', handleOutSideClick, true);

    return () => {
      document.removeEventListener('click', handleOutSideClick, true);
    };
  }, [handleOutSideClick]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePageScroll = () => {
      setIsOpen(false);
      setIsMaxError(false);
      setIsMinError(false);
    };

    window.addEventListener('scroll', handlePageScroll, true);

    return () => {
      window.removeEventListener('scroll', handlePageScroll, true);
    };
  }, [isOpen]);

  const getFormatErrorMsg = (input: string): string => {
    if (decimalPlaces === 2 && /^[0-9]*\.?[0-9]*$/.test(input)) {
      return 'dbi_number_range_two_decimal_tip';
    }
    return 'dbi_revenue_error_format_tip';
  };

  const handleInputChange = (val: string, type: 'min' | 'max', targetVal?: string) => {
    const value = val?.replace(/[,， ]/g, '');
    if (type === 'min') {
      setMinValue(value);

      if (!value) {
        setIsMinError(false);
        return;
      }
      if (!numberPattern.test(value)) {
        setIsMinError(true);
        setErrorMsg(getFormatErrorMsg(value));
        return;
      }

      if (Number(value) > BMK) {
        setMinValue(`${BMK}`);
      }

      const targetValue = Number(value) > BMK ? BMK : value;
      const maxVal = targetVal ? targetVal : maxValue;
      if (maxVal && Number(targetValue) > Number(maxVal)) {
        setIsMinError(true);
        setErrorMsg('dbi_min_greater_max');
        return;
      }

      setIsMinError(false);
      if (isMaxError) {
        handleInputChange(maxValue, 'max', value);
      }
    } else {
      setMaxValue(value);
      if (!value) {
        setIsMaxError(false);
        return;
      }
      if (!numberPattern.test(value)) {
        setIsMaxError(true);
        setErrorMsg(getFormatErrorMsg(value));
        return;
      }

      if (Number(value) > BMK) {
        setMaxValue(`${BMK}`);
      }
      const minVal = targetVal ? targetVal : minValue;
      if (minVal && Number(value) < Number(minVal)) {
        setIsMaxError(true);
        setErrorMsg('dbi_max_less_min');
        return;
      }

      setIsMaxError(false);

      if (isMinError) {
        handleInputChange(minValue, 'min', value);
      }
    }
  };

  const numberRangeTitle = useMemo(() => {
    if (minValue && maxValue) {
      return `${newMathFormat(minValue, true)} ~ ${newMathFormat(
        maxValue,
        true,
      )}`;
    }

    if (minValue && !maxValue) {
      return `${newMathFormat(minValue, true)} ~ `;
    }

    if (!minValue && maxValue) {
      return `~ ${newMathFormat(maxValue, true)}`;
    }

    return <span className='dbi_number-range-select-input-range'>
      {formatMessage({ id: 'dbi_input_range' })}
    </span>;
  }, [minValue, maxValue, locale]);

  const handleDeleteNumberRange = (type: 'min' | 'max') => {
    if (type === 'max') {
      setMaxValue('');
      setIsMaxError(false);
      if (inputMaxRef?.current) {
        inputMaxRef?.current?.focus();
      }

    } else {
      setMinValue('');
      setIsMinError(false);
      if (inputMinRef?.current) {
        inputMinRef?.current?.focus();
      }
    }
  };

  const handleInputClick = (type: 'min' | 'max') => {
    if (!clickSetDefault) return;
    if (type === 'max' && !maxValue) {
      handleInputChange('250000', 'max');
    }
    if (type === 'min' && !minValue) {
      handleInputChange('20000', 'min');
    }
  };

  const renderRangeField = (
    type: 'min' | 'max',
    fieldValue: string,
    isError: boolean,
    inputRef: React.RefObject<HTMLInputElement>,
    placeholderKey: string,
  ) => {
    const inputNode = (
      <>
        <Input
          ref={inputRef}
          value={fieldValue}
          placeholder={formatMessage({ id: placeholderKey })}
          onClick={() => handleInputClick(type)}
          onChange={(val) => handleInputChange(val, type)}
          style={{ paddingRight: fieldValue ? 30 : 0 }}
          className={classnames([
            { 'is-error': isError },
            'dbi-number-range-input',
            { 'dbi-number-range-input--with-unit': unitText },
          ])}
        />
        {fieldValue ? (
          <Icon
            className="dbi-number-range-icon"
            type="dismiss"
            onClick={() => handleDeleteNumberRange(type)}
          />
        ) : null}
      </>
    );

    if (unitText) {
      return (
        <InputAdornment
          className="dbi-number-range-box"
          before={unitText}
          style={{ display: 'flex' }}
        >
          {inputNode}
        </InputAdornment>
      );
    }

    return (
      <div className="dbi-number-range-box" style={{ display: 'flex', position: 'relative' }}>
        {inputNode}
      </div>
    );
  };

  return (
    <Dropdown
      ref={dropDown}
      className={classnames("select-item", "dbi-number-range-select-box", className)}
      overlayClassName="dbi-number-range-select-popover"
      // @ts-ignore
      onClick={() => setIsOpen(!isOpen)}
      open={isOpen}
      button={
        <div className="range-flex">
          <span className="dbi-number-range-select-title">
            {formatMessage({
              id: title || 'dbi_number_range',
              defaultMessage: titleDefaultMessage,
            })}
          </span>
          <span>{numberRangeTitle}</span>
        </div>
      }
      style={{ width: width || 300 }}
      appearance="button"
      clickClose={false}
    >
      <div style={{ padding: 16 }} ref={popover}>
        <div className="dbi-number-range-select-input">
          {renderRangeField('min', minValue, isMinError, inputMinRef, leftPlaceholder || 'dbi_minimum')}
          <span className="dbi-number-range-select-divide">-</span>
          {renderRangeField('max', maxValue, isMaxError, inputMaxRef, rightPlaceholder || 'dbi_maximum')}
        </div>
        {isMinError || isMaxError ? (
          <div className="dbi-number-range-select-input-error">
            {formatMessage({ id: errorMsg })}
          </div>
        ) : null}

        <div className="dbi-number-btn">
          <Button
            type="primary"
            className="dbi-number-btn-left"
            onClick={handleConfirm}
            disabled={isMinError || isMaxError}
          >
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          <Button
            className="dbi-number-btn-right"
            onClick={() => {
              setIsOpen(false);
              setIsMaxError(false);
              setIsMinError(false);
            }}
          >
            {formatMessage({ id: 'dbi_cancel' })}
          </Button>
        </div>
      </div>
    </Dropdown>
  );
};

export default NumberRange;
